import { Link } from "react-router-dom";
import { Phone, MapPin, Facebook, Instagram, ArrowLeft, Images, Newspaper, GitCompareArrows, Tag } from "lucide-react";
import { useContactSettings } from "@/hooks/useContactSettings";

const quickPages = [
  { label: "معرض الأعمال", desc: "شاهد أحدث مشاريعنا", path: "/معرض-الأعمال", Icon: Images, gradient: "from-amber-500 to-orange-600" },
  { label: "المدونة", desc: "مقالات ونصائح", path: "/المدونة", Icon: Newspaper, gradient: "from-emerald-500 to-teal-600" },
  { label: "مقارنة المنتجات", desc: "قارن واختر الأفضل", path: "/مقارنة", Icon: GitCompareArrows, gradient: "from-sky-500 to-blue-600" },
  { label: "العروض والخصومات", desc: "وفّر أكثر الآن", path: "/العروض", Icon: Tag, gradient: "from-rose-500 to-red-600" },
];

const linkGroups = [
  {
    title: "أقسام الموقع",
    links: [
      { label: "الرئيسية", path: "/" },
      { label: "من نحن", path: "/من-نحن" },
      { label: "معرض الأعمال", path: "/معرض-الأعمال" },
      { label: "العروض والخصومات", path: "/العروض" },
      { label: "المدونة", path: "/المدونة" },
    ],
  },
  {
    title: "الخدمات",
    links: [
      { label: "خدماتنا", path: "/خدماتنا" },
      { label: "طلب تصميم خاص", path: "/طلب-تصميم" },
      { label: "مقارنة المنتجات", path: "/مقارنة" },
      { label: "تقدير التكلفة", path: "/خدماتنا" },
    ],
  },
  {
    title: "الطلبات والحجز",
    links: [
      { label: "حجز موعد", path: "/حجز-موعد" },
      { label: "طلب طوارئ", path: "/طوارئ" },
      { label: "تتبع الطلب", path: "/تتبع-الطلب" },
      { label: "برنامج الولاء", path: "/برنامج-الولاء" },
      { label: "برنامج الإحالة", path: "/الإحالة" },
    ],
  },
  {
    title: "أدوات ذكية",
    links: [
      { label: "تصميم ذكي AI", path: "/تصميم-ذكي" },
      { label: "خريطة المشاريع", path: "/خريطة-المشاريع" },
      { label: "تواصل معنا", path: "/تواصل-معنا" },
    ],
  },
];

export default function Footer() {
  const { settings, getMainPhone, getWhatsAppLink } = useContactSettings();
  const mainPhone = getMainPhone();

  const phoneNumbers = [
    { label: "النجارة", nums: [settings.phone_carpentry_1, settings.phone_carpentry_2].filter(Boolean) },
    { label: "الألمنيوم", nums: [settings.phone_aluminum_1, settings.phone_aluminum_2].filter(Boolean) },
    { label: "الحديد", nums: [settings.phone_iron_1, settings.phone_iron_2].filter(Boolean) },
    { label: "المدير العام", nums: [settings.phone_manager].filter(Boolean) },
  ].filter(g => g.nums.length > 0);

  return (
    <footer className="bg-card border-t-2 border-primary/20">
      <div className="container mx-auto px-4 pt-10 pb-12 md:py-16">
        {/* Quick access cards - prominent on mobile */}
        <div className="mb-10">
          <h2 className="text-center text-lg md:text-xl font-bold text-foreground mb-1">تنقّل سريع</h2>
          <p className="text-center text-xs md:text-sm text-muted-foreground mb-5">أهم الصفحات في متناول يدك</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {quickPages.map(({ label, desc, path, Icon, gradient }) => (
              <Link
                key={path}
                to={path}
                className={`group relative overflow-hidden rounded-2xl bg-gradient-to-br ${gradient} p-4 md:p-5 shadow-lg hover:shadow-2xl active:scale-95 transition-all duration-300 min-h-[120px] flex flex-col justify-between text-white`}
              >
                <div className="absolute -top-6 -left-6 w-24 h-24 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform" />
                <div className="relative flex items-center justify-between">
                  <div className="w-10 h-10 md:w-11 md:h-11 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                    <Icon className="h-5 w-5 md:h-6 md:w-6" />
                  </div>
                  <ArrowLeft className="h-4 w-4 opacity-70 group-hover:-translate-x-1 transition-transform" />
                </div>
                <div className="relative">
                  <div className="font-bold text-sm md:text-base leading-tight mb-0.5">{label}</div>
                  <div className="text-[11px] md:text-xs opacity-90 leading-snug">{desc}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Main grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 mb-10">
          {/* Brand */}
          <div className="col-span-2">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-10 h-10 rounded-lg bg-gradient-woody flex items-center justify-center shadow-sm">
                <span className="text-primary-foreground font-bold text-lg">ش</span>
              </div>
              <div>
                <span className="font-bold text-lg text-foreground block leading-tight">مجمع الشويع الصناعي</span>
                <span className="text-[11px] text-primary font-medium">الموقع الرسمي والوحيد</span>
              </div>
            </div>
            <p className="text-muted-foreground text-sm leading-relaxed mb-5">
              متخصصون في جميع أعمال النجارة والألمنيوم والحديد بأعلى جودة وأفضل الأسعار في صعدة، اليمن.
            </p>

            {/* Contact numbers */}
            <div className="space-y-2">
              {phoneNumbers.map(group => (
                <div key={group.label}>
                  <span className="text-[11px] text-muted-foreground font-semibold uppercase tracking-wider">{group.label}</span>
                  {group.nums.map(num => (
                    <a key={num} href={`tel:+967${num}`} className="flex items-center gap-2 text-foreground/80 hover:text-primary text-sm transition-colors group">
                      <Phone className="h-3 w-3 text-primary/60 group-hover:text-primary" />
                      <span dir="ltr" className="font-medium">+967 {num}</span>
                    </a>
                  ))}
                </div>
              ))}
              <div className="flex items-center gap-2 text-foreground/80 text-sm mt-2">
                <MapPin className="h-3.5 w-3.5 text-primary/60" />
                <span>حيدان، صعدة، اليمن</span>
              </div>
            </div>

            {/* Social */}
            <div className="flex items-center gap-2 mt-4">
              <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center text-primary hover:bg-primary hover:text-primary-foreground transition-all" aria-label="واتساب">
                <Phone className="h-4 w-4" />
              </a>
              {settings.facebook_link && (
                <a href={settings.facebook_link} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center text-primary hover:bg-primary hover:text-primary-foreground transition-all" aria-label="فيسبوك">
                  <Facebook className="h-4 w-4" />
                </a>
              )}
              {settings.instagram_link && (
                <a href={settings.instagram_link} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center text-primary hover:bg-primary hover:text-primary-foreground transition-all" aria-label="إنستغرام">
                  <Instagram className="h-4 w-4" />
                </a>
              )}
            </div>
          </div>

          {/* Link groups */}
          {linkGroups.map((group) => (
            <div key={group.title}>
              <h3 className="font-bold text-foreground mb-4 text-sm">{group.title}</h3>
              <div className="flex flex-col gap-2.5">
                {group.links.map((link) => (
                  <Link
                    key={link.path + link.label}
                    to={link.path}
                    className="text-muted-foreground hover:text-primary text-sm transition-all flex items-center gap-1.5 group hover:translate-x-[-4px]"
                  >
                    <ArrowLeft className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity text-primary" />
                    <span className="group-hover:underline underline-offset-2">{link.label}</span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Copyright */}
        <div className="border-t border-border pt-6 flex flex-col items-center gap-3">
          <p className="text-foreground text-sm font-medium text-center">
            جميع الحقوق محفوظة لدى المهندس <strong>عبدالله طيب الشويع</strong> — هاتف: <a href="tel:+967777196039" className="text-primary hover:underline" dir="ltr">777196039</a>
          </p>
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <Link to="/سياسة-الخصوصية" className="hover:text-primary transition-colors hover:underline underline-offset-2">سياسة الخصوصية</Link>
            <span>•</span>
            <Link to="/شروط-الاستخدام" className="hover:text-primary transition-colors hover:underline underline-offset-2">شروط الاستخدام</Link>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>✨</span>
            <span>تم بناء وتطوير الموقع بواسطة المهندس <strong className="text-foreground">عبدالله الشويع</strong> — <a href="tel:+967777196039" className="text-primary hover:underline" dir="ltr">777196039</a></span>
          </div>
        </div>
      </div>
    </footer>
  );
}
